import { useCallback } from 'react';
import { useRequiredParamsSetter } from '../../../../context/TargetingSettingsContext';
import {
  type Element,
  type RequiredParamsType,
  RequiredTargetingSectionKeys,
  TargetingElementKeys,
} from '../../../../context/types';
import { useDeviceInitialState } from '../../../../hooks/initialState/useDeviceInitialState';
import { useFormatInitialState } from '../../../../hooks/initialState/useFormatInitialState';
import { useElementOperations } from '../../../../hooks/useElementOperations';
import { useFormatOperations } from '../../../../hooks/useFormatOperations';
import { enableTVDevices } from '../utils/enableTVDevices';
import { restoreDefaultOnClickToSection } from '../utils/restoreDefaultOnClickToSection';
import { useOneTimeHandlers } from './useOneTimeHandlers';

export const useCTVPopupActions = () => {
  const set = useRequiredParamsSetter();
  const { handleCTVSelect } = useFormatOperations();
  const { getOneTimeHandlers } = useOneTimeHandlers();
  const { disableElementByKey, enableElementByKey } = useElementOperations();
  const deviceInitialState = useDeviceInitialState();
  const formatInitialState = useFormatInitialState();

  const getDeviceOneTimeHandlers = useCallback(
    (prev: RequiredParamsType): Element[] =>
      getOneTimeHandlers({
        prev,
        sectionKey: RequiredTargetingSectionKeys.DEVICE,
        oneTimeAction: (set, sectionKey) => enableTVDevices(sectionKey, set, enableElementByKey),
        initialState: deviceInitialState,
      }),
    [getOneTimeHandlers, enableElementByKey, deviceInitialState]
  );

  const getFormatOneTimeHandlers = useCallback(
    (prev: RequiredParamsType): Element[] =>
      getOneTimeHandlers({
        prev,
        sectionKey: RequiredTargetingSectionKeys.FORMAT,
        oneTimeAction: set =>
          restoreDefaultOnClickToSection({
            sectionKey: RequiredTargetingSectionKeys.DEVICE,
            defaultState: deviceInitialState,
            set,
          }),
        initialState: formatInitialState,
      }),
    [getOneTimeHandlers, deviceInitialState, formatInitialState]
  );

  const onConfirmed = useCallback(() => {
    set((prev: RequiredParamsType) => {
      handleCTVSelect(prev.sections);

      return { ...prev };
    });
  }, [handleCTVSelect, set]);

  const onCancel = useCallback(() => {
    set((prev: RequiredParamsType) => {
      const updatedFormatElements = getFormatOneTimeHandlers(prev);
      const updatedDeviceElements = disableElementByKey(getDeviceOneTimeHandlers(prev), TargetingElementKeys.DEVICE_TV);

      return {
        ...prev,
        sections: {
          ...prev.sections,
          [RequiredTargetingSectionKeys.FORMAT]: {
            ...prev.sections[RequiredTargetingSectionKeys.FORMAT],
            elements: updatedFormatElements,
          },
          [RequiredTargetingSectionKeys.DEVICE]: {
            ...prev.sections[RequiredTargetingSectionKeys.DEVICE],
            elements: updatedDeviceElements,
          },
        },
      };
    });
  }, [set, getFormatOneTimeHandlers, getDeviceOneTimeHandlers, disableElementByKey]);

  return { onConfirmed, onCancel };
};
